<template>
  <section class="task-status" aria-labelledby="task-status-title">
    <button
      class="task-status__column task-status__column--tasks"
      type="button"
      v-tooltip:bottom="'Task Details'"
      aria-label="Open tasks page"
      @click="goto_tasks"
    >
      <h2 id="task-status-title">Tasks</h2>
    </button>

    <div
      class="task-status__column task-status__column--active"
      tabindex="0"
      aria-label="Active tasks and activity over the last seven days"
    >
      <strong>{{ isLoading ? '—' : activeTaskCount }}</strong>
      <span>Active</span>
      <div v-if="activityBars.length > 0" class="task-status__activity-chart" role="tooltip">
        <span class="task-status__activity-title">Tasks per day · last 7 days</span>
        <div class="task-status__activity-bars">
          <div
            v-for="day in activityBars"
            :key="day.key"
            class="task-status__activity-day"
            :aria-label="`${day.dateLabel}: ${day.value} tasks`"
            :title="`${day.dateLabel}: ${day.value} tasks`"
          >
            <span class="task-status__activity-value">{{ day.value }}</span>
            <span class="task-status__activity-bar" :style="{ height: `${day.height}%` }" />
            <span class="task-status__activity-label">{{ day.label }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="task-status__column task-status__column--funds">
      <div class="task-status__funds-value">
        <strong>{{ formatMoney(fundsRemaining) }}</strong>
        <span>USD</span>
      </div>
      <button
        v-if="warnings.length > 0"
        class="task-status__warning-toggle"
        type="button"
        :aria-expanded="warningsOpen"
        aria-controls="status-funds-warnings"
        :aria-label="`${warnings.length} funds warning${warnings.length === 1 ? '' : 's'}`"
        title="View funds warnings"
        @click="warningsOpen = !warningsOpen"
      >
        <SVGIcon icon-name="warning" width="18px" height="18px" />
        <span>{{ warnings.length }}</span>
      </button>
    </div>

    <div class="task-status__column task-status__column--action">
      <button
        class="task-status__add-funds"
        type="button"
        v-tooltip:bottom="'Add Funds'"
        aria-label="Add funds"
        @click="goto_admin_topup"
      >
        Add Funds
      </button>
    </div>

    <div
      v-if="warningsOpen && warnings.length > 0"
      id="status-funds-warnings"
      class="task-status__warnings"
    >
      <ul>
        <li v-for="warning in warnings" :key="warning.key">{{ warning.text }}</li>
      </ul>
    </div>
  </section>
</template>

<script setup>
import {
  computed, onBeforeUnmount, onMounted, ref,
} from 'vue';
import { useStore } from 'vuex';
import SVGIcon from '@/components/SVGIcon.vue';
import { goto_admin_topup, goto_tasks } from '@/assets/js/links.js';

const store = useStore();
const RECONNECT_DELAY_MS = 10000;
const CURRENCY_FORMATTER = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const taskSummary = ref(null);
const funds = ref(null);
const isLoading = ref(true);
const connectionState = ref('connecting');
const connectionError = ref('');
const warningsOpen = ref(false);
const hasConnected = ref(false);

let socket = null;
let reconnectTimer = null;
let isUnmounted = false;

const activeTaskCount = computed(() => numberOrNull(taskSummary.value?.active) ?? 0);

const activityBars = computed(() => {
  const activity = taskSummary.value?.activity;
  if (!Array.isArray(activity)) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - 6 + index);
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    return { key, date, value: 0 };
  });
  const daysByKey = new Map(days.map((day) => [day.key, day]));

  activity.forEach((task) => {
    const timestamp = new Date(task?.time);
    if (Number.isNaN(timestamp.getTime())) return;

    const key = `${timestamp.getFullYear()}-${timestamp.getMonth()}-${timestamp.getDate()}`;
    const day = daysByKey.get(key);
    if (day) day.value += 1;
  });

  const maximum = Math.max(1, ...days.map((day) => day.value));
  return days.map(({ key, date, value }) => ({
    key,
    label: date.toLocaleDateString(undefined, { weekday: 'short' }),
    dateLabel: date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
    value,
    height: value === 0 ? 2 : Math.max(12, (value / maximum) * 100),
  }));
});

const fundsRemaining = computed(() => {
  const total = numberOrNull(funds.value?.total_funds_and_credits);
  if (total !== null) return total;

  const currentFunds = numberOrNull(funds.value?.funds);
  const credits = numberOrNull(funds.value?.credits);
  if (currentFunds !== null || credits !== null) return (currentFunds || 0) + (credits || 0);
  return null;
});

const warnings = computed(() => (Array.isArray(funds.value?.warnings) ? funds.value.warnings : [])
  .map((warning, index) => ({
    key: `${warning?.organization_id ?? 'org'}-${warning?.key ?? index}-${index}`,
    text: String(warning?.value ?? warning?.message ?? '').trim(),
  }))
  .filter((warning) => warning.text));

const connectionLabel = computed(() => ({
  connecting: 'Connecting to live status…',
  connected: 'Live updates connected',
  reconnecting: 'Connection lost. Reconnecting…',
  error: 'Live updates unavailable',
}[connectionState.value] || 'Connecting to live status…'));

function numberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatMoney(value) {
  return value === null ? '—' : CURRENCY_FORMATTER.format(value);
}

function scheduleReconnect() {
  if (isUnmounted || reconnectTimer) return;
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, RECONNECT_DELAY_MS);
}

function handleMessage(event) {
  try {
    const payload = JSON.parse(event.data);
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      throw new Error('Unexpected funds status payload.');
    }
    if (payload.error) {
      connectionError.value = String(payload.error.message || payload.error);
      return;
    }

    if (payload.task_summary && typeof payload.task_summary === 'object') {
      taskSummary.value = payload.task_summary;
    }
    if (payload.funds && typeof payload.funds === 'object') funds.value = payload.funds;
    isLoading.value = false;
    connectionError.value = '';
    connectionState.value = 'connected';
    hasConnected.value = true;
  } catch (error) {
    connectionError.value = error?.message || 'Unable to read live status.';
    connectionState.value = 'error';
  }
}

function connect() {
  if (isUnmounted || (socket && [WebSocket.OPEN, WebSocket.CONNECTING].includes(socket.readyState))) return;
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const token = store.state.user.token;
  const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
  const wsUrl = `${protocol}://${window.location.host}/ws/funds_status${tokenQuery}`;

  connectionState.value = hasConnected.value ? 'reconnecting' : 'connecting';
  connectionError.value = '';

  try {
    const currentSocket = new WebSocket(wsUrl);
    socket = currentSocket;

    currentSocket.onopen = () => {
      if (socket === currentSocket) {
        connectionState.value = 'connected';
        connectionError.value = '';
      }
    };
    currentSocket.onmessage = handleMessage;
    currentSocket.onerror = () => {
      if (socket === currentSocket) {
        connectionState.value = 'error';
        connectionError.value = 'Could not connect to live status. Retrying…';
      }
    };
    currentSocket.onclose = () => {
      if (socket === currentSocket) socket = null;
      if (!isUnmounted) {
        connectionState.value = 'reconnecting';
        if (!connectionError.value) connectionError.value = 'Connection closed. Retrying…';
        scheduleReconnect();
      }
    };
  } catch (error) {
    connectionState.value = 'error';
    connectionError.value = error?.message || 'Could not connect to live status.';
    scheduleReconnect();
  }
}

onMounted(connect);

onBeforeUnmount(() => {
  isUnmounted = true;
  if (reconnectTimer) window.clearTimeout(reconnectTimer);
  reconnectTimer = null;
  if (socket) {
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    if ([WebSocket.OPEN, WebSocket.CONNECTING].includes(socket.readyState)) socket.close();
    socket = null;
  }
});
</script>

<style lang="scss" scoped>
.task-status {
  width: 250px;
  height: 40px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  padding: 4px;
  border: 1px solid var(--color-grey-300, #b5b9bf);
  border-radius: 6px;
  background: var(--color-primary-400);
  color: var(--button-text-color);
}


.task-status__column {
  display: flex;
  flex: 1 1 0;
  align-items: center;
  min-width: 0;
  height: 100%;
}

.task-status__column--tasks {
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  height: 28px;
  padding: 0 6px;
  border: 1px solid var(--color-grey-300, #b5b9bf);
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-radius: 4px;
}

.task-status__column--tasks:hover,
.task-status__add-funds:hover {
  background-color: #588669;
}

.task-status__column--tasks:focus-visible,
.task-status__add-funds:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.task-status__column--tasks h2 {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 600;
}

.task-status__column--active {
  position: relative;
  flex-direction: column;
  justify-content: center;
  gap: 1px;
  text-align: center;
  cursor: help;
}

.task-status__activity-chart {
  position: absolute;
  z-index: 20;
  top: calc(100% + 6px);
  left: 50%;
  width: 184px;
  padding: 8px 10px 6px;
  transform: translateX(-50%);
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  color: #242424;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.12s ease;
}

.task-status__column--active:hover .task-status__activity-chart,
.task-status__column--active:focus .task-status__activity-chart {
  opacity: 1;
  visibility: visible;
}

.task-status__activity-title {
  display: block;
  margin-bottom: 5px;
  font-size: 0.62rem;
  font-weight: 600;
  white-space: nowrap;
}

.task-status__activity-bars {
  display: grid;
  height: 48px;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  align-items: end;
  gap: 4px;
}

.task-status__activity-day {
  display: flex;
  height: 100%;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.task-status__activity-value,
.task-status__activity-label {
  font-size: 0.52rem;
  line-height: 1;
  white-space: nowrap;
}

.task-status__activity-bar {
  width: 10px;
  max-height: 28px;
  min-height: 2px;
  border-radius: 2px 2px 0 0;
  background: #588669;
}

.task-status__column--active strong,
.task-status__funds-value strong {
  font-size: 0.72rem;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}

.task-status__connection::before {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #a1a1aa;
  content: '';
}

.task-status__connection.is-connected::before { background: #15803d; }
.task-status__connection.is-error::before,
.task-status__connection.is-reconnecting::before { background: #b45309; }

.task-status__column--active > span,
.task-status__funds-value > span {
  font-size: 0.6rem;
  line-height: 1.1;
  opacity: 0.82;
}

.task-status__column--funds {
  justify-content: flex-end;
}

.task-status__column--action {
  flex: 0 0 auto;
  justify-content: center;
}

.task-status__add-funds {
  display: flex;
  width: auto;
  height: 28px;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border: 1px solid var(--color-grey-300, #b5b9bf);
  border-radius: 4px;
  background: transparent;
  color: inherit;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.task-status__funds-value {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
}

.task-status__funds-value strong { white-space: nowrap; }

.task-status__warning-toggle {
  display: inline-flex;
  flex: 0 0 auto;
  min-width: 30px;
  min-height: 30px;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 3px 5px;
  border: 1px solid #f0b44c;
  border-radius: 4px;
  background: #fff8e6;
  color: #8a4b08;
  cursor: pointer;
}

.task-status__warning-toggle span { font-size: 0.68rem; font-weight: 700; }

.task-status__warnings {
  position: absolute;
  z-index: 2;
  top: calc(100% + 6px);
  right: 8px;
  width: min(360px, calc(100vw - 32px));
  padding: 10px 12px;
  border: 1px solid #f0b44c;
  border-radius: 4px;
  background: #fff;
  color: #713f12;
  font-size: 0.86rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
}

.task-status__warnings ul { margin: 0; padding: 0; list-style: none; }
.task-status__warnings li + li { margin-top: 6px; }

@media (max-width: 600px) {
  .task-status { padding: 6px 8px; }
  .task-status__column--tasks h2 { font-size: 0.85rem; }
  .task-status__column--active > span,
  .task-status__funds-value > span { font-size: 0.55rem; }
  .task-status__column--active strong,
  .task-status__funds-value strong { font-size: 0.66rem; }
  .task-status__column--funds { gap: 5px; }
  .task-status__warning-toggle { min-width: 26px; min-height: 26px; padding: 2px 4px; }
}
</style>
